/**
 * Classify Scanbrix workspaces.
 * - Auto "Practice Space" — tutorial workspace created on signup (exclude from client funnel).
 * - User-created Practice — `workspace_type: practice` with a real name/address (user choice).
 * - Client — `workspace_type: client` — real project workspaces for deliverables.
 */

function workspaceTags(doc) {
  const raw = doc?.tags;
  if (!Array.isArray(raw)) return [];
  return raw.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
}

function workspaceType(doc) {
  return String(doc?.workspace_type ?? doc?.workspaceType ?? doc?.type ?? "")
    .trim()
    .toLowerCase();
}

function workspaceName(doc) {
  return String(doc?.workspace_name ?? doc?.name ?? doc?.title ?? "").trim();
}

function workspaceNameLower(doc) {
  return workspaceName(doc).toLowerCase();
}

function hasPracticePlaceholderAddress(doc) {
  const street = String(doc?.street ?? doc?.street_address ?? doc?.streetAddress ?? "")
    .trim()
    .toLowerCase();
  const state = String(doc?.state ?? doc?.city ?? "").trim().toUpperCase();
  const zip = String(doc?.zip_code ?? doc?.zipCode ?? doc?.postal_code ?? "").trim();
  return street === "practice" && state === "NA" && zip === "00000";
}

const DEFAULT_PRACTICE_DESCRIPTION =
  "a free practice workspace to try your first scan";

function hasDefaultPracticeDescription(doc) {
  const desc = String(doc?.description ?? "").trim().toLowerCase();
  return desc.includes(DEFAULT_PRACTICE_DESCRIPTION);
}

/**
 * Auto-created signup tutorial workspace ("Practice Space").
 * Not the same as a user-created workspace where they chose Practice type.
 */
export function isDefaultPracticeSpace(doc) {
  if (!doc || typeof doc !== "object") return false;
  const name = workspaceNameLower(doc);
  if (name === "practice space" && hasPracticePlaceholderAddress(doc)) return true;
  if (hasDefaultPracticeDescription(doc) && hasPracticePlaceholderAddress(doc)) return true;
  if (workspaceTags(doc).includes("practice") && hasPracticePlaceholderAddress(doc)) return true;
  return false;
}

/** User chose Client when creating a workspace (real project). */
export function isClientWorkspace(doc) {
  if (!doc || typeof doc !== "object") return false;
  return workspaceType(doc) === "client";
}

/** User chose Practice when creating a workspace (not the auto signup Practice Space). */
export function isUserCreatedPracticeWorkspace(doc) {
  if (!doc || typeof doc !== "object") return false;
  if (isDefaultPracticeSpace(doc)) return false;
  return workspaceType(doc) === "practice";
}

/**
 * User-created workspace via Create Workspace flow (client or practice type).
 * Excludes only the auto signup "Practice Space" (Practice, NA 00000).
 */
export function isUserCreatedWorkspace(doc) {
  if (!doc || typeof doc !== "object") return false;
  if (isDefaultPracticeSpace(doc)) return false;
  const type = workspaceType(doc);
  return type === "client" || type === "practice";
}

/** @deprecated Use isDefaultPracticeSpace — kept for clarity in older call sites. */
export function isPracticeWorkspace(doc) {
  return isDefaultPracticeSpace(doc);
}

export function workspaceIdFromDoc(doc) {
  if (!doc || typeof doc !== "object") return "";
  const id =
    doc.workspace_id ??
    doc.workspaceId ??
    doc.workspaceID ??
    doc.id ??
    null;
  return id != null && String(id).trim() ? String(id).trim() : "";
}

/** Workspace ids for auto-created Practice Space (for member-row filtering). */
export function defaultPracticeWorkspaceIdSet(workspaces = []) {
  const ids = new Set();
  for (const ws of workspaces) {
    if (!isDefaultPracticeSpace(ws)) continue;
    const id = workspaceIdFromDoc(ws);
    if (id) ids.add(id);
  }
  return ids;
}

/** Workspace ids for user-created client or practice workspaces. */
export function userCreatedWorkspaceIdSet(workspaces = []) {
  const ids = new Set();
  for (const ws of workspaces) {
    if (!isUserCreatedWorkspace(ws)) continue;
    const id = workspaceIdFromDoc(ws);
    if (id) ids.add(id);
  }
  return ids;
}
